import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { EditButtonLabel } from 'owa-locstrings/lib/strings/editbuttonlabel.locstring.json';
import findInlineComposeViewState from 'owa-mail-compose-actions/lib/utils/findInlineComposeViewState';
import {
    lazyCreateReply,
    lazyLoadDraftToCompose,
    lazyTrySaveAndCloseCompose,
} from 'owa-mail-compose-actions';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import {
    EditMode,
    lazyEditAndReply,
    lazyEditInDraft,
    SxSStoreData,
    getOrCreateSxSStoreData,
} from 'owa-sxs-store';
import { PreviewPaneMode } from 'owa-sxsdata';
import isVisio from '../../utils/isVisio';
import { EditAndReplyButtonLabel } from './getEditButtons.locstring.json';
import { composeStore, ComposeTarget, ComposeOperation } from 'owa-mail-compose-store';
import { getOfficeAppFromAttachmentId } from './getEditDropDownButton';
import setIsDirty from 'owa-mail-compose-actions/lib/actions/setIsDirty';

async function loadDraftToCompose(
    itemId: ClientItemId,
    forceInsertSignatureOperation: ComposeOperation
) {
    const loadDraftToCompose = await lazyLoadDraftToCompose.import();
    await loadDraftToCompose(itemId.Id, ComposeTarget.SxS, {
        forceInsertSignatureOperation: forceInsertSignatureOperation,
    });
}

async function trySaveAndCloseCompose(
    conversationId: string,
    composeId: string
): Promise<ClientItemId> {
    /* This is to check if we already created a reply by clicking Reply or Reply all button.
    If it has been created, we want to re-use the draft instead of creating a new reply */
    const composeViewState =
        findInlineComposeViewState(conversationId) ||
        (composeId && composeStore.viewStates.get(composeId));

    if (composeViewState) {
        setIsDirty(composeViewState);
        const trySaveAndCloseCompose = await lazyTrySaveAndCloseCompose.import();
        await trySaveAndCloseCompose(composeViewState);
        return composeViewState.itemId as ClientItemId;
    } else {
        return null;
    }
}

async function createReply(referenceItemId: ClientItemId): Promise<ClientItemId> {
    const mailboxInfo = referenceItemId.mailboxInfo;
    const groupId =
        mailboxInfo && mailboxInfo.type == 'GroupMailbox' ? mailboxInfo.mailboxSmtpAddress : null;
    const createReply = await lazyCreateReply.import();
    const message = await createReply(
        referenceItemId.Id,
        true, // isReplyAll
        groupId,
        // For SXS view we shall not override the server behavior where upon r/R/F it marks the reference item as read
        // which means this will be the one case where the item may get removed from unread filter but its still less disruptive as SxS view will be on top of LV.
        false /* suppressServerMarkReadOnReplyOrForward */
    );

    const itemId: ClientItemId = {
        ...message.ItemId,
        mailboxInfo: referenceItemId.mailboxInfo,
    };

    return itemId;
}

export function isCloudyAndUsingJsApi(sxsStore: SxSStoreData): boolean {
    return (
        (isFeatureEnabled('doc-SxS-jsApi-ODB-Base') ||
            isFeatureEnabled('doc-SxS-jsApi-ODB-BaseNoEditString')) &&
        sxsStore.extendedViewState.isCloudy &&
        sxsStore.viewState &&
        sxsStore.viewState.previewPane &&
        sxsStore.viewState.previewPane.mode === PreviewPaneMode.Wac &&
        !!sxsStore.viewState.previewPane.jsApiInfo
    );
}

function isClassicVisio(sxsStore: SxSStoreData): boolean {
    if (sxsStore.extendedViewState.isCloudy) {
        return false;
    }

    const attachmentId = sxsStore.extendedViewState.attachmentId;
    const attachmentModel = attachmentId && getAttachment(attachmentId);

    return isVisio(attachmentModel.model);
}

export default function getEditButtons(sxsId: string): FilterableMenuItem[] {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    // Edit in draft button
    const editInDraftButton = new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: loc(EditButtonLabel),
        [FilterableMenuItemArgs.icon]: ControlIcons.Edit,
        [FilterableMenuItemArgs.onClick]: async () => {
            const editInDraft = await lazyEditInDraft.import();
            editInDraft(loadDraftToCompose, sxsId);
        },
        [FilterableMenuItemArgs.shouldShow]: () => {
            return (
                (sxsStore.readingPaneState ||
                    (sxsStore.composeId && composeStore.viewStates.get(sxsStore.composeId))) &&
                sxsStore.viewState?.previewPane &&
                sxsStore.viewState.previewPane.mode === PreviewPaneMode.Wac &&
                sxsStore.editState.editAllowed &&
                sxsStore.editState.mode === EditMode.View &&
                sxsStore.editState.itemId &&
                sxsStore.editState.isDraft &&
                !isCloudyAndUsingJsApi(sxsStore) &&
                !isClassicVisio(sxsStore)
            );
        },
        [FilterableMenuItemArgs.ariaLabel]: loc(EditButtonLabel),
    });

    // Edit and reply button
    const editAndReplyButton = new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: loc(EditAndReplyButtonLabel),
        [FilterableMenuItemArgs.icon]: ControlIcons.PencilReply,
        [FilterableMenuItemArgs.onClick]: () => {
            editAndReply(sxsId);
        },
        [FilterableMenuItemArgs.shouldShow]: () => {
            const isGroup =
                sxsStore.editState.itemId?.mailboxInfo &&
                sxsStore.editState.itemId.mailboxInfo.type === 'GroupMailbox';
            return (
                !(
                    isFeatureEnabled('wvn-sxsEditButtonSplit') &&
                    getOfficeAppFromAttachmentId(sxsStore.extendedViewState.attachmentId) !== null
                ) &&
                sxsStore.readingPaneState &&
                sxsStore.viewState?.previewPane &&
                sxsStore.viewState.previewPane.mode === PreviewPaneMode.Wac &&
                sxsStore.editState.editAllowed &&
                sxsStore.editState.mode === EditMode.View &&
                sxsStore.editState.itemId &&
                !sxsStore.editState.isDraft &&
                (!isGroup || !isConsumer()) &&
                !isCloudyAndUsingJsApi(sxsStore) &&
                !isClassicVisio(sxsStore)
            );
        },
        [FilterableMenuItemArgs.ariaLabel]: loc(EditAndReplyButtonLabel),
        [FilterableMenuItemArgs.displayOption]:
            isFeatureEnabled('wvn-productizeSTCChicletAndPreview') &&
            sxsStore.extendedViewState.saveToCloudStatus === SaveToCloudStatus.succeeded
                ? DisplayOption.Overflow
                : DisplayOption.Left,
    });

    return [editInDraftButton, editAndReplyButton];
}

export async function editAndReply(sxsId: string) {
    const editAndReply = await lazyEditAndReply.import();
    editAndReply(loadDraftToCompose, createReply, trySaveAndCloseCompose, sxsId);
}
